/* eslint-disable no-unused-vars */
import { UserSettlementSummary } from "components/UserSettlementSummary";
import { motion } from "framer-motion";
import { Header } from "components/Common/Header";
import { findTeeTimeByUser } from "utils/teeTime";
import { useTeeTimes } from "hooks/useTeeTimes";
import { useUserData } from "hooks/useUserData";
import { useParams } from "react-router-dom";
import { useEvent } from "hooks/useEvent";
import { useAuth } from "hooks/useAuth";
import { useState } from "react";
import "./styles.css";

import Text from "components/Common/Text";
import { isMatchCompleted } from "utils/match";
import { FinalScorecardSkeleton } from "./FinalScorecardSkeleton";
import { ScorecardResults } from "./components/scorecardResults";
import { ScorecardItem } from "./components/scorecardItem";
import MatchInfo from "../Common/MatchInfo";
import { Tabs } from "./components/Tabs";
import MatchPlay from "./matchPlay";
import ScorecardLegend from "./components/scorecardLegend";

function FinalScorecard() {
  const { eventId } = useParams();
  const event = useEvent(eventId);
  const teeTimes = useTeeTimes(eventId);

  const { user } = useAuth();
  const userData = useUserData(user);

  const [userPhone, setUserPhone] = useState("");

  const onUserSelected = (phone) => {
    setUserPhone(phone);
  };

  const userTeeTime = findTeeTimeByUser(teeTimes, userData);

  const { teamsPlay, totalStake } = event ?? {};

  const addTeamNameToEveryUserObject = (teeTimesData) =>
    teeTimesData.map(
      (teeTime) =>
        teeTime.teams?.map((tm) => {
          // eslint-disable-next-line no-param-reassign
          tm.users = tm.users.map((us) => ({
            ...us,
            teamName: tm.name,
            individual: tm.users.length < tm.teamSize,
            playingEventId: teeTime.id,
            ways: tm.ways,
            ball1: tm.ball1,
            ball2: tm.ball2,
          }));
          return tm;
        }) ?? [],
    );

  const usersArray = (teeTimesData2, isTeamsPlay) =>
    teeTimesData2.length && isTeamsPlay
      ? addTeamNameToEveryUserObject(teeTimesData2)
          .flatMap((item) => item.flatMap((team) => team.users))
          .concat()
      : teeTimesData2
          .flatMap(
            (item) =>
              item.users &&
              item.users.map((us) => ({
                ...us,
                teeTimeString: item?.teeTimeString,
                teeTime: item?.teeTime,
              })),
          )
          .filter((el) => el !== undefined);

  const concat = (x, y) => x.concat(y);

  const usersList =
    teeTimes.length && usersArray(teeTimes, teamsPlay)?.reduce(concat, []);

  const findActiveUserInEventPlayers = () => {
    if (!userData?.uid) {
      return false;
    }
    if (
      teeTimes
        ?.map((i) => i.teams)
        .flat()
        ?.users?.some((u) => u.uid === userData?.uid)
    ) {
      return false;
    }

    return true;
  };

  const tabList = [
    {
      title: "Scorecard",
      component: (
        <>
          <ScorecardItem
            users={usersList}
            onHoleClicked={() => {}}
            hasOwnProperty
            final
            holeHandicap={event?.holes?.[0]?.handicap}
            skins={event?.skins}
            carryOver={event?.carryOver}
            ballCount={event?.ballCount}
            teamsPlay={event?.teamsPlay}
            matchType={event?.matchType}
          />
          <ScorecardLegend skins={event?.skins} />
        </>
      ),
    },
    {
      title: "Payouts",
      component: userPhone ? (
        <UserSettlementSummary
          eventId={eventId}
          playerId={userPhone}
          onGoBack={() => setUserPhone("")}
          teamsPlay={teamsPlay}
          totalStake={totalStake}
        />
      ) : (
        <ScorecardResults
          teeTimes={teeTimes}
          matchType={event?.matchType}
          onUserSelected={onUserSelected}
          usersArray={usersList}
          teamsPlay={teamsPlay}
          secondBetMatchType={
            event?.bets.length > 0 ? event?.bets[0]?.matchType : null
          }
          ballCount={event?.ballCount}
        />
      ),
    },
  ];

  if (
    event?.matchType === 0 || event?.bets.length > 0
      ? event?.bets[0]?.matchType === 0
      : null
  ) {
    tabList.splice(1, 0, {
      title: "Quota",
      component: (
        <ScorecardItem
          users={usersList}
          onHoleClicked={() => {}}
          hasOwnProperty
          final
          holeHandicap={event?.holes?.[0]?.handicap}
          skins={event?.skins}
          quota
          ballCount={event?.ballCount}
          teamsPlay={event?.teamsPlay}
          matchType={event?.matchType}
          secondBetMatchType={
            event?.bets.length > 0 ? event?.bets[0]?.matchType : null
          }
        />
      ),
    });
  }

  if (event?.matchType === 3 || event?.matchType === 8) {
    tabList.splice(1, 0, {
      title: "Match",
      component: findActiveUserInEventPlayers() ? (
        <MatchPlay
          data={usersList}
          teams={
            event.numberOfTeeTimes === 1
              ? teeTimes?.map((i) => i.teams)
              : teeTimes
                  ?.map((i) => i.teams)
                  .flat()
                  .map((team) => ({
                    teamName: team?.name,
                    data: team,
                    playerTeam: team?.users?.some(
                      (u) => u.uid === userData?.uid,
                    ),
                  })) || []
          } // TODO: fix this!
          teamsPlay={teamsPlay}
          numberOfTeeTimes={event.numberOfTeeTimes}
          ballCount={event?.ballCount}
          competedMatch={isMatchCompleted(event ?? {})}
        />
      ) : (
        <Text
          ta="center"
          style={{
            color: "gray",
            height: 50,
            padding: 20,
          }}
          as="P1"
        >
          This section is exclusively for players participating in this match :(
        </Text>
      ),
    });
  }

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  return (
    <div>
      <Header
        event={event}
        userData={userData}
        teeTime={userTeeTime}
        showPayout
        usersArray={usersArray(teeTimes, teamsPlay)}
        hideQr
      />
      <MatchInfo event={event} hideTabs />
      {teeTimes?.length ? (
        <>
          <Tabs
            tabList={tabList}
            activeIndex={(i) => setActiveTabIndex(i)}
            initialIndex={userPhone?.length ? 2 : 0}
          />
          <div
            style={{
              height: 2,
              width: "100%",
              backgroundColor:
                tabList[activeTabIndex].title === "Match"
                  ? "rgba(41, 119, 164)"
                  : "rgba(30, 38, 46, 0.7)",
            }}
          />

          {tabList[activeTabIndex].component}
        </>
      ) : (
        <FinalScorecardSkeleton />
      )}
    </div>
  );
}

export { FinalScorecard };
